import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";

const IAM_DETAILS_QUERY = `
  query GetIamDetails($email: String!) {
    GetIamDetails(email: $email)
  }
`;
const IAM_AUDIT_MUTATION = `
  mutation AuditIamMission($input: IamAuditInput!) {
    AuditIamMission(input: $input)
  }
`;

const statusLabels = {
  pending: "待審核",
  approved: "已通過",
  rejected: "未通過",
};

const taskLabels = {
  iam_creation: "建立使用者",
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const headerCellSx = {
  whiteSpace: "nowrap",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: "0.02em",
  backgroundColor: "#f3f5f9",
  borderBottom: "1px solid #e0e0e0",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
  fontSize: "0.8rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const missionLogColumnSx = {
  time: { width: "33%", whiteSpace: "nowrap" },
  status: { width: "12%", whiteSpace: "nowrap" },
  message: {
    width: "39%",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  actor: { width: "16%", whiteSpace: "nowrap" },
};

const pendingColumnWidths = {
  id: "96px",
  type: "140px",
  applicant: "320px",
  status: "120px",
  created: "220px",
  view: "120px",
  actions: "220px",
};

const statusColumnWidths = {
  id: "96px",
  type: "140px",
  status: "120px",
  created: "220px",
  updated: "220px",
  email: "320px",
  view: "120px",
};

const pendingColumnOrder = ["id", "type", "applicant", "status", "created", "view", "actions"];
const statusColumnOrder = ["id", "type", "status", "created", "updated", "email", "view"];
const pendingTableMinWidth = "1236px";
const statusTableMinWidth = "1236px";
const READABLE_TEXT_COLOR = "#344767";
const READABLE_MUTED_TEXT_COLOR = "#5B6B88";

const renderCellValue = (value) => {
  const display = value === null || value === undefined || value === "" ? "-" : String(value);
  return (
    <MDBox
      sx={{
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "#000",
      }}
      title={display}
    >
      {display}
    </MDBox>
  );
};

const renderColGroup = (widths, order) => (
  <colgroup>
    {order.map((key) => (
      <col key={key} style={{ width: widths[key] }} />
    ))}
  </colgroup>
);

function TaskReview() {
  const { userData } = useUserContext() || {};
  const email = userData?.email ?? "";
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const isRecordNotFound = (err) =>
    String(err || "")
      .toLowerCase()
      .includes("record not found");
  const [view, setView] = useState("pending");
  const [tab, setTab] = useState("all");
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditAction, setAuditAction] = useState("");
  const [auditComment, setAuditComment] = useState("");
  const [auditTarget, setAuditTarget] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [requestMissions, setRequestMissions] = useState([]);
  const [auditMissions, setAuditMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDetails = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: IAM_DETAILS_QUERY,
          variables: { email },
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        const message = json.errors[0]?.message || "GraphQL error";
        if (isRecordNotFound(message)) {
          setRequestMissions([]);
          setAuditMissions([]);
        } else {
          throw new Error(message);
        }
      } else {
        const payload = json?.data?.GetIamDetails;
        const data = payload?.data || {};
        setRequestMissions(toArray(data.request_missions));
        setAuditMissions(toArray(data.audit_missions));
      }
    } catch (err) {
      console.error(err);
      setError("載入任務資料失敗");
    } finally {
      setLoading(false);
    }
  }, [backendUrl, email]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const pendingMissions = useMemo(
    () => auditMissions.filter((mission) => mission.status === "pending"),
    [auditMissions]
  );

  const requestStatusMissions = useMemo(
    () =>
      requestMissions.filter(
        (mission) => mission.status === "approved" || mission.status === "rejected"
      ),
    [requestMissions]
  );

  const summaryCards = useMemo(
    () => [{ label: "建立使用者", count: pendingMissions.length, active: true }],
    [pendingMissions.length]
  );

  const handleOpenDetail = (task) => {
    setSelectedTask(task);
    setOpenDetail(true);
  };

  const handleOpenAudit = (task, action) => {
    setAuditTarget(task);
    setAuditAction(action);
    setAuditComment("");
    setAuditError("");
    setAuditOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditOpen(false);
    setAuditTarget(null);
    setAuditAction("");
    setAuditComment("");
    setAuditError("");
  };

  const handleSubmitAudit = async () => {
    if (!email) {
      setAuditError("找不到登入者 Email");
      return;
    }
    if (!auditTarget?.id) {
      setAuditError("任務資訊錯誤");
      return;
    }
    if (!auditComment.trim()) {
      setAuditError("請填寫留言");
      return;
    }

    setAuditLoading(true);
    setAuditError("");
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: IAM_AUDIT_MUTATION,
          variables: {
            input: {
              email,
              mission_id: Number(auditTarget.id),
              action: auditAction,
              comment: auditComment.trim(),
            },
          },
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }
      handleCloseAudit();
      fetchDetails();
    } catch (err) {
      console.error(err);
      setAuditError("審核失敗，請稍後再試");
    } finally {
      setAuditLoading(false);
    }
  };

  const selectedSubject = selectedTask?.subject || {};
  const missionInfoRows = useMemo(
    () => [
      { label: "任務ID", value: selectedTask?.id },
      {
        label: "類型",
        value: taskLabels[selectedTask?.mission_type] || selectedTask?.mission_type,
      },
      { label: "狀態", value: statusLabels[selectedTask?.status] || selectedTask?.status },
      { label: "描述", value: selectedTask?.description },
      { label: "建立時間", value: selectedTask?.created_at.split(".")[0].replace("T", " ") },
      { label: "更新時間", value: selectedTask?.updated_at.split(".")[0].replace("T", " ") },
    ],
    [selectedTask]
  );

  const subjectInfoRows = useMemo(
    () => [
      { label: "目標 Email", value: selectedSubject?.target_email },
      {
        label: "角色名稱",
        value: selectedSubject?.target_role_name || selectedSubject?.target_role_id,
      },
      {
        label: "第三方名稱",
        value: selectedSubject?.target_third_party_name || selectedSubject?.target_third_party_id,
      },
      { label: "名", value: selectedSubject?.target_first_name },
      { label: "姓", value: selectedSubject?.target_last_name },
    ],
    [selectedSubject]
  );

  const missionLogs = useMemo(() => {
    console.log(selectedTask?.mission_logs);
    return toArray(selectedTask?.mission_logs);
  }, [selectedTask]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={3} pb={4}>
                {view === "pending" ? (
                  <MDBox>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6">待審核任務</MDTypography>
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={() => setView("status")}
                      >
                        查看審核狀態
                      </MDButton>
                    </MDBox>
                    <MDBox display="flex" gap={2} mb={3}>
                      {summaryCards.map((card) => (
                        <MDBox
                          key={card.label}
                          sx={{
                            flex: "0 0 220px",
                            backgroundColor: card.active ? "#4c7fd1" : "#f3f5f9",
                            color: card.active ? "#fff" : "inherit",
                            borderRadius: "12px",
                            px: 2,
                            py: 1.5,
                            boxShadow: card.active ? "0 6px 16px rgba(76, 127, 209, 0.35)" : "none",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="medium">
                            {card.label}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            display="block"
                            color={card.active ? "white" : "text"}
                          >
                            {card.count}項待處理
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                    {loading && (
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        mb={1}
                        sx={{ color: READABLE_TEXT_COLOR }}
                      >
                        載入中...
                      </MDTypography>
                    )}
                    {error && (
                      <MDTypography variant="button" color="error" fontWeight="medium" mb={1}>
                        {error}
                      </MDTypography>
                    )}
                    <TableContainer
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        border: "1px solid #eee",
                        overflowX: "auto",
                        "& table": {
                          width: "100%",
                          minWidth: pendingTableMinWidth,
                          tableLayout: "fixed",
                        },
                        "& th, & td": {
                          boxSizing: "border-box",
                        },
                      }}
                    >
                      <Table size="small" sx={{ width: "100%" }}>
                        {renderColGroup(pendingColumnWidths, pendingColumnOrder)}
                        <TableHead
                          sx={{
                            display: "table-header-group",
                            padding: 0,
                            borderRadius: 0,
                          }}
                        >
                          <TableRow>
                            <TableCell sx={headerCellSx}>任務ID</TableCell>
                            <TableCell sx={headerCellSx}>類型</TableCell>
                            <TableCell sx={headerCellSx}>申請人</TableCell>
                            <TableCell sx={headerCellSx}>狀態</TableCell>
                            <TableCell sx={headerCellSx}>申請時間</TableCell>
                            <TableCell
                              sx={{
                                ...headerCellSx,
                                textAlign: "left",
                              }}
                            >
                              查看資料
                            </TableCell>
                            <TableCell sx={headerCellSx} align="right">
                              {" "}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingMissions.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell sx={bodyCellSx}>{renderCellValue(row.id)}</TableCell>
                              <TableCell sx={bodyCellSx}>
                                {renderCellValue(taskLabels[row.mission_type]) || row.mission_type}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                {renderCellValue(row.subject?.target_email)}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                {renderCellValue(statusLabels[row.status] || row.status)}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                {renderCellValue(row.created_at.split(".")[0].replace("T", " "))}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...bodyCellSx,
                                  textAlign: "left",
                                }}
                              >
                                <MDButton
                                  variant="text"
                                  color="info"
                                  size="small"
                                  onClick={() => handleOpenDetail(row)}
                                >
                                  詳細資料
                                </MDButton>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <MDButton
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleOpenAudit(row, "rejected")}
                                  >
                                    拒絕
                                  </MDButton>
                                  <MDButton
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={() => handleOpenAudit(row, "approved")}
                                  >
                                    通過
                                  </MDButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!pendingMissions.length && !loading && (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                無待審核任務
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6">審核狀態</MDTypography>
                      <MDButton variant="text" color="info" onClick={() => setView("pending")}>
                        返回待審核任務
                      </MDButton>
                    </MDBox>
                    <MDBox display="flex" gap={2} mb={2}>
                      {summaryCards.map((card, idx) => (
                        <MDBox
                          key={card.label}
                          sx={{
                            flex: "0 0 200px",
                            backgroundColor: idx === 0 ? "#4c7fd1" : "#f3f5f9",
                            color: idx === 0 ? "#fff" : "inherit",
                            borderRadius: "12px",
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <MDTypography variant="button" fontWeight="medium">
                            {card.label}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            display="block"
                            color={idx === 0 ? "white" : "text"}
                          >
                            {card.count}項待處理
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                    <Tabs
                      value={tab}
                      onChange={(_, value) => setTab(value)}
                      textColor="inherit"
                      indicatorColor="primary"
                      sx={{ mb: 2 }}
                    >
                      <Tab value="all" label="所有申請" />
                      <Tab value="approved" label="已通過" />
                      <Tab value="rejected" label="未通過" />
                    </Tabs>
                    <TableContainer
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        border: "1px solid #eee",
                        overflowX: "auto",
                        "& table": {
                          width: "100%",
                          minWidth: statusTableMinWidth,
                          tableLayout: "fixed",
                        },
                        "& th, & td": {
                          boxSizing: "border-box",
                        },
                      }}
                    >
                      <Table size="small" sx={{ width: "100%" }}>
                        {renderColGroup(statusColumnWidths, statusColumnOrder)}
                        <TableHead
                          sx={{
                            display: "table-header-group",
                            padding: 0,
                            borderRadius: 0,
                          }}
                        >
                          <TableRow>
                            <TableCell sx={headerCellSx}>任務ID</TableCell>
                            <TableCell sx={headerCellSx}>類型</TableCell>
                            <TableCell sx={headerCellSx}>狀態</TableCell>
                            <TableCell sx={headerCellSx}>申請時間</TableCell>
                            <TableCell sx={headerCellSx}>更新時間</TableCell>
                            <TableCell sx={headerCellSx}>目標 Email</TableCell>
                            <TableCell
                              sx={{
                                ...headerCellSx,
                                textAlign: "left",
                              }}
                            >
                              查看資料
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {requestStatusMissions
                            .filter((row) => {
                              if (tab === "approved") return row.status === "approved";
                              if (tab === "rejected") return row.status === "rejected";
                              return true;
                            })
                            .map((row) => (
                              <TableRow key={row.id}>
                                <TableCell sx={bodyCellSx}>{renderCellValue(row.id)}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(
                                    taskLabels[row.mission_type] || row.mission_type
                                  )}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(statusLabels[row.status] || row.status)}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(row.created_at.split(".")[0].replace("T", " "))}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(row.updated_at.split(".")[0].replace("T", " "))}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(row.subject?.target_email)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...bodyCellSx,
                                    textAlign: "left",
                                  }}
                                >
                                  <MDButton
                                    variant="text"
                                    color="info"
                                    size="small"
                                    onClick={() => handleOpenDetail(row)}
                                  >
                                    詳細資料
                                  </MDButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          {!requestStatusMissions.length && !loading && (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                無申請任務
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3}>
          <MDTypography variant="h6">任務詳情</MDTypography>
          <IconButton onClick={() => setOpenDetail(false)} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <DialogContent>
          <Stack spacing={3}>
            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="medium"
                mb={1}
                display="block"
                sx={{ color: READABLE_TEXT_COLOR }}
              >
                任務資訊
              </MDTypography>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableBody>
                    {missionInfoRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell sx={{ width: 140, color: READABLE_MUTED_TEXT_COLOR }}>
                          {row.label}
                        </TableCell>
                        <TableCell>{row.value || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </MDBox>

            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="medium"
                mb={1}
                display="block"
                sx={{ color: READABLE_TEXT_COLOR }}
              >
                目標資訊
              </MDTypography>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableBody>
                    {subjectInfoRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell sx={{ width: 140, color: READABLE_MUTED_TEXT_COLOR }}>
                          {row.label}
                        </TableCell>
                        <TableCell>{row.value || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </MDBox>

            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="medium"
                mb={1}
                display="block"
                sx={{ color: READABLE_TEXT_COLOR }}
              >
                任務紀錄
              </MDTypography>
              <TableContainer sx={{ borderRadius: 2, border: "1px solid #eee" }}>
                <Table size="small" sx={{ whiteSpace: "nowrap" }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell sx={missionLogColumnSx.time}>時間</TableCell>
                      <TableCell sx={missionLogColumnSx.status}>狀態</TableCell>
                      <TableCell sx={missionLogColumnSx.message}>訊息</TableCell>
                      <TableCell sx={missionLogColumnSx.actor}>操作者名稱</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missionLogs.map((log) => (
                      <TableRow key={log.ID}>
                        <TableCell>{log.created_at.split(".")[0].replace("T", " ")}</TableCell>
                        <TableCell>{statusLabels[log.status] || log.status}</TableCell>
                        <TableCell>{log.message}</TableCell>
                        <TableCell>{log.actor_iam ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                    {!missionLogs.length && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          無任務紀錄
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={auditOpen}
        onClose={handleCloseAudit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3}>
          <MDTypography variant="h6">
            {auditAction === "approved" ? "通過任務" : "拒絕任務"}
          </MDTypography>
          <IconButton onClick={handleCloseAudit} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <DialogContent>
          <Stack spacing={2.5}>
            <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
              任務ID：{auditTarget?.id ?? "-"}，目標 Email：
              {auditTarget?.subject?.target_email || "-"}
            </MDTypography>
            <MDInput
              label="留言"
              multiline
              rows={4}
              fullWidth
              value={auditComment}
              onChange={(event) => setAuditComment(event.target.value)}
            />
            {auditError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {auditError}
              </MDTypography>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <MDButton variant="text" color="secondary" onClick={handleCloseAudit}>
                取消
              </MDButton>
              <MDButton
                variant="gradient"
                color={auditAction === "approved" ? "info" : "error"}
                onClick={handleSubmitAudit}
                disabled={auditLoading}
              >
                確認
              </MDButton>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default TaskReview;
